import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {Avatar} from 'shells/avatar/avatar';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {ErrorState} from 'shells/error-state/error-state';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TrashIcon} from 'shells/icon/svg/trash-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {PopoverInfo} from 'shells/popover-info/popover-info';
import {Table} from 'shells/table/table';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';
import {TwoLine} from 'shells/two-line/two-line';

import styles from './recipient-modal-list.module.css';

type DripProgression = {
    id: string;
    audienceMember: {
        id: string;
        audience: {
            id: string;
        };
        entity: {
            id: string;
            name: string;
            avatarUrl: string;
            intials: string;
            htmlUrlPath: string;
            emailAddresses: {
                primary: {
                    value: {
                        display: string;
                    };
                };
            };
            accounts: {
                primary: {
                    id: string;
                    name: string;
                };
            };
        };
    };
    nextEditionSendTime: number;
};

type Props = {
    dripProgressions: DripProgression[];
    emailTemplateId: string;
    isLoading: boolean;
    error: string | null | undefined;
    showMessageBodyPreviewModal: (emailTemplateId: string, entityId: string) => void;
    onCancelDripProgression: (dripProgressionId: string) => Promise<any>;
};

export function RecipientModalList(props: Props) {
    const {emailTemplateId, showMessageBodyPreviewModal, onCancelDripProgression} = props;

    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Person name',
                accessor: (dripProgression: DripProgression) =>
                    dripProgression.audienceMember.entity,
                dataType: 'component',
                Cell: ({cell}: any) => {
                    return (
                        <TwoLine
                            avatar={
                                <Avatar
                                    type='contacts'
                                    avatarUrl={cell.value.avatarUrl}
                                    initials={cell.value.initials}
                                />
                            }
                            iconSize={32}
                            topRow={
                                // @ts-expect-error
                                <Link variant='primary' as={RouterLink} to={cell.value.htmlUrlPath}>
                                    {cell.value.name}
                                </Link>
                            }
                            bottomRow={
                                cell.value.accounts.primary ? cell.value.accounts.primary.name : ''
                            }
                            isLargeWithSmallBottomRow={true}
                        />
                    );
                },
                minWidth: 150,
                width: 250,
                maxWidth: 250,
            },
            {
                id: 'email',
                Header: 'Email',
                accessor: (dripProgression: DripProgression) =>
                    dripProgression.audienceMember.entity,
                dataType: 'component',
                Cell: ({cell}: any) => {
                    return (
                        <p>
                            {cell.value.emailAddresses.primary ? (
                                cell.value.emailAddresses.primary.value.display
                            ) : (
                                <div className={styles['missing-email']}>
                                    No valid email address
                                    <PopoverInfo
                                        info='This person needs a valid email address to receive this message'
                                        bodyColor='dark'
                                        variant='error'
                                    />
                                </div>
                            )}
                        </p>
                    );
                },
                shouldTruncate: true,
                minWidth: 150,
                width: 240,
                maxWidth: 240,
            },
            {
                id: 'remainingTime',
                Header: 'Time until sending',
                accessor: (dripProgression: DripProgression) => dripProgression,
                dataType: 'component',
                Cell: ({cell}: any) => {
                    const nextEditionSendTime = cell.value.nextEditionSendTime;
                    const timeNow = Date.now() / 1000;
                    const isLessThanTenMinutes = nextEditionSendTime - timeNow < 10;

                    return (
                        <div className={styles['time-container']}>
                            <div className='flex'>
                                {isLessThanTenMinutes ? (
                                    <p>Sending soon</p>
                                ) : (
                                    <>
                                        <RelativeTimestamp
                                            datetime={cell.value.nextEditionSendTime}
                                            format='toNow'
                                            hideAgoString={true}
                                        />
                                        &nbsp;until sending
                                    </>
                                )}
                            </div>
                            <ActionMenu
                                options={[
                                    {
                                        label: 'View message',
                                        variant: 'secondary',
                                        icon: <EyeIcon wrapWithDiv={false} size={18} />,
                                        handleSelect: () => {
                                            const entityId = cell.value.audienceMember.entity.id;
                                            showMessageBodyPreviewModal(emailTemplateId, entityId);
                                        },
                                    },
                                    {
                                        label: 'Remove from drip',
                                        variant: 'destructive',
                                        icon: <TrashIcon size={16} />,
                                        handleSelect: () => {
                                            onCancelDripProgression(cell.value.id);
                                        },
                                    },
                                ]}
                            />
                        </div>
                    );
                },
                minWidth: 200,
                width: 240,
                maxWidth: 250,
            },
        ],
        [emailTemplateId, showMessageBodyPreviewModal, onCancelDripProgression]
    );

    if (!props.isLoading && !props.error && props.dripProgressions.length === 0) {
        return (
            <div className='full-width'>
                <EmptyStateLarge
                    title='This message has no queued recipients'
                    subtitle={<p>When people are added to the queue, they’ll show up here</p>}
                    icon={
                        <div style={{width: 120, height: 120}}>
                            <Illustration src='cactus-empty-state' width='100%' height='100%' />
                        </div>
                    }
                />
            </div>
        );
    }

    return (
        <div className={styles['recipient-table']}>
            <Table
                isResizable={true}
                hasHeaderBorder={true}
                hasHeaderCellBorders={true}
                hasHoverState={false}
                isCompact={true}
                stickyHeaders={true}
                hasBorders={true}
                isCheckboxSelectable={false}
                clickingSelectsRow={false}
                disableFilters={true}
                disableSorting={true}
                disableSortRemove={true}
                shouldPreventSelectAll={false}
                // @ts-expect-error
                columns={columns}
                data={props.dripProgressions}
                specialStates={[
                    {
                        shouldRender: () => Boolean(props.error),
                        component: <ErrorState errorMessage={props.error} />,
                    },
                    {
                        shouldRender: () => props.isLoading,
                        component: <LoadingPage padding={16} />,
                    },
                ]}
            />
        </div>
    );
}
